import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { CheckCircle, Loader, Crown, ArrowRight } from "lucide-react";

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const checkingRef = useRef(false);

  useEffect(() => {
    // Note: pending_auth is already handled by SubscriptionSuccessRoute in App.js
    // This component will only render after the user is already logged in
    
    const sessionId = searchParams.get("session_id");
    // Always check localStorage directly for most reliable token access
    const authToken = token || localStorage.getItem("token");
    
    console.log("SubscriptionSuccess: session_id =", sessionId);
    console.log("SubscriptionSuccess: token from context =", token ? "exists" : "none");
    console.log("SubscriptionSuccess: token from localStorage =", localStorage.getItem("token") ? "exists" : "none");
    
    if (!sessionId) {
      // No session_id, just go to dashboard
      console.log("No session_id, redirecting to parent dashboard");
      navigate("/parent");
      return;
    }
    
    // If we have a token (from either source), check payment status
    if (authToken && !checkingRef.current) {
      checkingRef.current = true;
      checkPaymentStatus(sessionId, authToken);
    } else if (!authToken) {
      // Wait for token to be available from SubscriptionSuccessRoute
      console.log("Waiting for token to be available...");
      const timer = setTimeout(() => {
        const delayedToken = localStorage.getItem("token");
        if (delayedToken && !checkingRef.current) {
          checkingRef.current = true;
          checkPaymentStatus(sessionId, delayedToken);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [token, searchParams]);

  const checkPaymentStatus = async (sessionId, authToken) => {
    if (attempts >= 10) {
      setStatus("timeout");
      return;
    }

    try {
      console.log("Checking payment status with token:", authToken ? "present" : "missing");
      const { data } = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });
      
      console.log("Payment status response:", data);
      
      // Backend returns status: "success" when payment is complete
      if (data.status === "success" || data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      
      // Keep polling
      setAttempts(a => a + 1);
      setTimeout(() => checkPaymentStatus(sessionId, authToken), 2000);
    } catch (e) {
      console.error("Payment status check error:", e);
      // If 401/403, token might be invalid - don't retry
      if (e.response?.status === 401 || e.response?.status === 403) {
        setStatus("error");
        return;
      }
      // For other errors, we can retry a few times
      if (attempts < 3) {
        setAttempts(a => a + 1);
        setTimeout(() => checkPaymentStatus(sessionId, authToken), 2000);
      } else {
        setStatus("error");
      }
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <div className="text-center px-4">
          <Loader size={52} color="#9B5DE5" className="mx-auto animate-spin mb-4" />
          <p className="font-black text-xl text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
            Confirming your payment...
          </p>
          <p className="text-gray-500 text-sm mt-2">Please wait a moment</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}>
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ background: "linear-gradient(135deg, #06D6A0, #118AB2)" }}>
            <CheckCircle size={44} color="white" strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl font-black text-[#073B4C] mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
            Welcome to Premium!
          </h1>
          <p className="text-gray-500 mb-6">
            Thank you for subscribing! Your family now has access to all Bible Buds features.
          </p>
          
          <div className="bg-purple-50 rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-3 justify-center">
              <Crown size={24} color="#9B5DE5" />
              <span className="font-black text-[#9B5DE5]">Premium Active</span>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-gray-50 rounded-2xl p-4 mb-6 text-left">
            <p className="font-bold text-[#073B4C] mb-3 text-sm">Next Steps:</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-5 h-5 rounded-full bg-[#06D6A0] flex items-center justify-center text-white text-xs font-bold">1</div>
                <span className="text-gray-600">Add your children's profiles</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-5 h-5 rounded-full bg-[#118AB2] flex items-center justify-center text-white text-xs font-bold">2</div>
                <span className="text-gray-600">Start their first Bible lesson</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-5 h-5 rounded-full bg-[#9B5DE5] flex items-center justify-center text-white text-xs font-bold">3</div>
                <span className="text-gray-600">Set up weekly prizes for motivation</span>
              </div>
            </div>
          </div>

          <button onClick={() => navigate("/parent")}
            className="w-full text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}
            data-testid="go-to-dashboard-btn">
            Go to Dashboard <ArrowRight size={20} />
          </button>
        </div>
      </div>
    );
  }

  // Error or timeout state
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#F0F4FF" }}>
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-xl text-center">
        <h2 className="text-xl font-black text-[#073B4C] mb-3" style={{ fontFamily: "Fredoka, sans-serif" }}>
          {status === "timeout" ? "Taking longer than expected" : "Something went wrong"}
        </h2>
        <p className="text-gray-500 mb-6">
          {status === "timeout" 
            ? "Your payment may still be processing. Check your email for confirmation."
            : "There was an issue verifying your payment."}
        </p>
        <button onClick={() => navigate("/parent")}
          className="text-white font-bold px-6 py-3 rounded-xl"
          style={{ background: "#118AB2" }}>
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}
